<template>
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="secondary"
    />
  </div>
  <div
    v-else
    ref="contentRef"
    class="page"
  >
    <riseup-header
      :flow-config-id="otsJobConfig.flowConfigId"
      :force-background-color="'#EEF2F8'"
    />
    <div ref="content">
      <missing-data
        v-if="shouldShowMissingData"
        @connect-accounts-click="handleConnectAccountsClick"
      />
      <div>
        <six-months-summary
          :monthly-avg-income-spending="monthlyAvgIncomeSpending"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          :is-data-missing="isSixMonthsDataMissing"
          :valid-cashflow-months="validCashflowMonths"
          flow-type="borrowAndSave"
          @button-click="() => navigateToLink('LOAN')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <spending-categories
          :monthly-category-averages="monthlyCategoryAverages"
          :is-data-missing="isSpendingCategoriesDataMissing"
          @button-click="() => navigateToLink('LOAN')"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <in-out
          :is-data-missing="isInOutDataMissing"
          :last-four-months="lastFourMonths"
        />
        <tips
          :tips="tips"
          :total-savings="totalSavings"
          :is-data-missing="isTipsDataMissing"
          :show-background="!shouldShowMissingData"
          @connect-accounts-click="handleConnectAccountsClick"
        />
        <monthly-balance
          v-if="!shouldShowMissingData"
          :monthly-avg-disposable-income="monthlyAvgDisposableIncome"
          @explore-savings-click="() => navigateToLink('EXPLORE_SAVINGS')"
        />
        <answer-survey
          v-if="!shouldShowMissingData"
          @button-click="() => navigateToLink('START_SURVEY')"
        />
      </div>
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    :text="errorMessage"
    :reason="errorReason"
    @close="showErrorToast = false"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import config from '../../../config';
import Segment from '../../../Segment';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import ErrorToast from '../../Common/ErrorToast.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import MissingData from '../shared-components/MissingData.vue';
import SixMonthsSummary from '../shared-components/SixMonthsSummary.vue';
import SpendingCategories from '../shared-components/SpendingCategories.vue';
import Tips from '../shared-components/Tips.vue';
import AnswerSurvey from './components/AnswerSurvey.vue';
import MonthlyBalance from './components/MonthlyBalance.vue';
import InOut from './components/InOut.vue';
import { formatLastFourMonths } from '../../../utils/miscUtils';

const { insights, populateInsights } = useInsights();
const contentRef = ref(null);
const route = useRoute();
const { getOtsJobConfig } = useOtsJob();
const {
  monthlyCategoryAverages,
  monthlyAvgDisposableIncome,
  monthlyAvgIncomeSpending,
  loadCashflowData,
  isCashflowLoaded,
  monthlyInOut,
  validCashflowMonths,
} = useCashflow();
const NAVIGATION_DESTINATION = {
  EXPLORE_SAVINGS: 'riseup_wl_explore_savings',
  START_SURVEY: 'riseup_wl_start_survey',
};
const ERROR_MESSAGES = {
  INSIGHTS_ONLY: 'Failed to load your saving tips. We are looking into it. Try again later.',
  CASHFLOW_ONLY: 'Oops! There was an error with your account data. We are looking into it. Try again later.',
};

const tips = ref([]);
const totalSavings = ref(0);
const otsJobConfig = await getOtsJobConfig();
const sessionStore = useSessionStore();
const showErrorToast = ref(false);
const errorMessage = ref('');
const errorReason = ref('');

const {
  showContentLoader,
  toggleLoader,
} = useContentLoader();

// Centralized missing data logic
const isSixMonthsDataMissing = computed(() =>
  monthlyAvgIncomeSpending.value.income === 0
  || monthlyAvgIncomeSpending.value.spending === 0
  || validCashflowMonths.value < 3);

const isSpendingCategoriesDataMissing = computed(() =>
  monthlyCategoryAverages.value.length < 3);

const isTipsDataMissing = computed(() => tips.value.length === 0);

const isInOutDataMissing = computed(() => {
  if (!monthlyInOut.value || monthlyInOut.value.length < 2) {
    return true;
  }

  const itemsWithNonZeroValues = monthlyInOut.value.filter(month =>
    month.in !== 0 || month.out !== 0).length;
  const haveLessThan2months = itemsWithNonZeroValues < 2;
  const notAllMonthsDefined = !monthlyInOut.value.every(month => month.month);

  return haveLessThan2months || notAllMonthsDefined;
});

const shouldShowMissingData = computed(() =>
  isSixMonthsDataMissing.value
  || isSpendingCategoriesDataMissing.value
  || isTipsDataMissing.value
  || isInOutDataMissing.value);

const lastFourMonths = computed(() => formatLastFourMonths(monthlyInOut.value));

const handleErrors = (insightsFailed, cashflowFailed) => {
  if (!insightsFailed && !cashflowFailed) {
    return;
  }

  if (cashflowFailed) {
    errorMessage.value = ERROR_MESSAGES.CASHFLOW_ONLY;
    errorReason.value = 'cashflow_data_load_failed';
  } else {
    errorMessage.value = ERROR_MESSAGES.INSIGHTS_ONLY;
    errorReason.value = 'insights_data_load_failed';
  }

  showErrorToast.value = true;
};

onMounted(async () => {
  toggleLoader(true);
  const queryParam = route.query || null;
  const shouldReload = route.state?.shouldReload;

  let insightsFailed = false;
  let cashflowFailed = false;

  if (!insights.value || insights.value.length === 0 || shouldReload) {
    try {
      await populateInsights(otsJobConfig.jobId);
    } catch (error) {
      insightsFailed = true;
    }
  }

  Segment.trackUserGot('RiseupWLOtsCustomSuccessPageEntered', { fromEmail: queryParam ? queryParam.fromEmail : false, otsJobConfig });

  if ((insights.value && insights.value.length > 0) || shouldReload) {
    tips.value = insights.value.map(insight => ({
      text: insight.item.text,
      amount: insight.item.amount,
      index: insight.index,
    }));

    totalSavings.value = insights.value.reduce((sum, insight) => sum + insight.item.amount, 0);
  }

  if (!isCashflowLoaded.value || shouldReload) {
    try {
      await loadCashflowData(6);
    } catch (error) {
      cashflowFailed = true;
    }
  }

  handleErrors(insightsFailed, cashflowFailed);
  toggleLoader(false);

  Segment.trackUserGot('RiseupWLOtsCustomSuccessResults', {
    validCashflowMonths: validCashflowMonths.value,
    isSixMonthsDataMissing: isSixMonthsDataMissing.value,
    isSpendingCategoriesDataMissing: isSpendingCategoriesDataMissing.value,
    isTipsDataMissing: isTipsDataMissing.value,
    isInOutDataMissing: isInOutDataMissing.value,
    shouldShowMissingData: shouldShowMissingData.value,
    insightLoadFailed: insightsFailed,
    cashflowLoadFailed: cashflowFailed,
  });
});

const navigateToLink = (destination = 'EXPLORE_SAVINGS') => {
  const { customerId } = sessionStore;
  let url = `${config.get().ruthUrl}/ots?customer_id=${customerId}&to=${NAVIGATION_DESTINATION[destination]}`;

  if (destination === 'START_SURVEY') {
    Segment.trackUserGot('RiseupWLStartSurveyClicked', {
      gclid: Cookies.get('gclid'),
      userId: route.query?.userId,
      productId: route.query?.productId,
    });

    if (route.query?.userId && route.query?.productId) {
      url += `&userId=${route.query.userId}&productId=${route.query.productId}`;
    }
  }

  window.open(url, '_blank');
};

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('RiseupWLConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  window.location.href = await getTrueLayerUrl({ jobId: otsJobConfig.jobId, fromSuccess: true }, config.get().otsRiseupWLSuccessEnv);
};
</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.content {
  margin-top: 30px;
  padding-left: 25px;
  padding-right: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

h1 {
  font-weight: 100;
}

h1 b {
  font-weight: bold !important;
}

.page {
  background-color: #012577;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  font-family: 'SalaryFinance';
}

.page-section {
  color: white;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 35px;
}

.section-subtitle {
  font-size: 17px;
  font-family: 'SalaryFinance';
  margin-top: 25px;
  margin-bottom: 40px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.info-title {
  font-size: 36px;
  font-family: 'SalaryFinance';
  padding-top: 40px;
  padding-right: 25px;
  margin-bottom: 20px;
  text-align: left;
  width: 80%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 100 !important;
  line-height: 1;
}

.white {
  color: #FFFFFF;
}

.red {
  color: #F14241;
}

.font-32 {
  font-size: 32px;
}

.info-title b {
  font-weight: 700;
}

.divider {
  width: 100%;
  margin: 30px auto;
  height: 1px;
  border-bottom: 1px solid #002579;
  opacity: 20%;
}

.blue-button {
  background-color: #002579;
  color: white;
  width: 100%;
  border-radius: 12px;
  padding: 10px 0px;
  border: none;
  cursor: pointer;
  font-size: 18px;
  font-family: 'SalaryFinance';
  font-weight: 700;
  margin-top: 40px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 16px;
  padding-bottom: 18px;
}

.width-80 {
  width: 80%;
}

.width-90 {
  width: 90%;
}

.margin-top-20 {
  margin-top: 20px;
}

.secondary-button {
  background-color: #FFFFFF;
  border: 1px solid #002579;
  color: #002579;
  width: 100%;
  border-radius: 12px;
  cursor: pointer;
  font-size: 18px;
  font-family: 'SalaryFinance';
  font-weight: 700;
  margin-top: 15px;
  margin-bottom: 20px;
  margin-left: auto;
  margin-right: auto;
  display: block;
  padding-top: 19px;
  padding-bottom: 21px;
}

.button-container {
  width: 90%;
  margin: 0 auto;
}

.white-border {
  border: 1px solid #FFFFFF
}

.image-container {
  width: 90%;
  margin: 0 auto;
}

.image-container img {
  width: 100%;
  margin: 15px auto;
  position: relative;
  z-index: 1;
}

.light-blue-section {
  background-color: #EEF2F8;
  color: #002579;
}

.white-section {
  background-color: #FFFFFF;
  color: #002579;
}

.arrow-right {
  margin-left: 10px;
  font-family: 'SalaryFinance';
  color: #FD5F5A;
  font-size: 30px;
  vertical-align: sub;

  &.vertical-align-middle {
    vertical-align: middle;
  }
}

.top-title {
  font-size: 67px;
  font-family: 'SalaryFinance';
  line-height: 1;
  color: #002579;
  width: 80%;
  margin-left: 10%;
  text-align: left;
  margin-bottom: 20px;
}

.light-section-text {
  color: #002579;
}

.breakdown-table {
  width: 90%;
  background-color: white;
  box-shadow: 0px 16px 46.4px 0px #00000026;
  border-radius: 25px;
  padding: 25px;
  margin-top: 20px;
  z-index: 2;
  margin: 0 auto;
  position: relative;
  padding-top: 30px;
}

.breakdown-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  h1 {
    margin: 0;
    color: #002579;
    font-size: 25px;
  }

  h2 {
    margin: 0;
    color: #002579;
    font-size: 20px;
    font-weight: 700;
    margin-left: 10px;
  }

  h3 {
    margin: 0;
    color: #F14241;
    font-size: 20px;
  }

  .graph-icon {
    width: 20px;
    height: 20px;
  }
}

.breakdown-content {
  width: 100%;
}

.breakdown-row-no-border {
  display: flex;
  padding: 5px 5px;
  font-family: 'SalaryFinance';
}

.col {
  color: #0C3467;

  &.source {
    flex: 4;
    text-align: left;
  }

  &.total {
    flex: 1;
  }

  &.invest {
    flex: 1;
    text-align: right;
    font-weight: 700;
  }
}

.financial-support {
  margin: 20px auto;
  padding: 5px 20px;
}

.financial-support-header {
  padding-top: 20px;
}

.blue-background-color {
  background-color: #002579;
  height: 100px;
  z-index: 0;
  position: absolute;
  bottom: 0;
  width: 100%;
}
</style>
